// ============================================================
// Last-known Property Management payload, per owner.
//
// WHY THIS EXISTS
//
// The live backend runs at min-instances=0 (Backend/docs/DEPLOY-BACKEND-PROMPT.md,
// rule 7): a cold container takes ~28s to answer its first request and ~0.3s
// after that. An owner opening the app after an idle period therefore waited
// out the whole cold start before seeing a single number.
//
// The three ways to remove that wait all cost money — min-instances=1 (~$172/
// month for an idle instance), a scheduler pinging the service warm, or more
// CPU. This costs nothing: the portfolio an owner saw last time is written to
// the device, and the next open renders it immediately while the network call
// revalidates in the background. Cold start becomes invisible instead of
// expensive, because nobody is staring at a skeleton while it happens.
//
// It is deliberately NOT a general-purpose cache:
//   - keyed per owner, so switching accounts cannot show the wrong portfolio;
//   - versioned, so a payload-shape change invalidates rather than mis-renders;
//   - given an age limit, so a device left offline for days shows the loading
//     state and waits for real data rather than presenting stale rent figures
//     as current;
//   - always revalidated — it changes WHEN the owner sees the numbers, never
//     WHICH numbers are authoritative.
//
// PRIVACY: the payload carries tenant names, rents and Emirates IDs, so it is
// cleared on logout (InvestorPortal.handleLogout) alongside the auth token.
// The token already lives in this same store and grants access to all of this
// data from any network, so caching the response does not widen what an
// attacker with the device would get.
// ============================================================

const VERSION = 'v1';
const KEY = (ownerId: string) => `pmUnits:${VERSION}:${ownerId}`;

// Beyond this, show the loading state instead. Rent, tenancy and maintenance
// change on a scale of weeks, so a day-old portfolio is a fair first paint;
// a week-old one is not.
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

interface Entry {
  at: number;
  units: any[];
}

export function readPmUnits(ownerId: string): any[] | null {
  if (!ownerId) return null;
  try {
    const raw = localStorage.getItem(KEY(ownerId));
    if (!raw) return null;
    const entry: Entry = JSON.parse(raw);
    if (!entry || !Array.isArray(entry.units)) return null;
    if (!(entry.at > 0) || Date.now() - entry.at > MAX_AGE_MS) {
      localStorage.removeItem(KEY(ownerId));
      return null;
    }
    return entry.units;
  } catch {
    // Private mode, cleared site data, blocked storage, or a half-written
    // entry — all mean "no cache", never a crash on the way into the screen.
    return null;
  }
}

export function writePmUnits(ownerId: string, units: any[]): void {
  if (!ownerId || !Array.isArray(units)) return;
  try {
    localStorage.setItem(KEY(ownerId), JSON.stringify({ at: Date.now(), units } as Entry));
  } catch {
    // Over quota (a large portfolio on a device with little room) — drop this
    // owner's entry so the next write has a chance, and carry on. A cache that
    // cannot be written is not an error the owner should ever hear about.
    try { localStorage.removeItem(KEY(ownerId)); } catch { /* nothing to do */ }
  }
}

// Called on logout. Scans for this module's keys rather than taking an ownerId,
// so it cannot miss an account the user signed out of earlier on this device.
export function clearPmCache(): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('pmUnits:')) doomed.push(k);
    }
    doomed.forEach((k) => localStorage.removeItem(k));
  } catch { /* storage unavailable — nothing cached, nothing to clear */ }
}
